<template>
    <div class="ad-report-container">
        <div class="container-fluid p-0">
            <div class="row p-0">
                <!-- First Column with fixed width -->
                <div class="col-12 col-sm-auto fixed-width">
                    <div class="report-ad-header">
                        <ReportAdHeader :tableIsLoaded="tableIsLoaded" class="report-ad-header" />
                    </div>
                </div>
                <!-- Second Column that fills up the rest -->
                <div class="col graph-column">
                    <div class="report-ad-graph">
                        <ReportAdGraph :graphText="graphText" class="report-ad-graph" :measureTypeSelected="measureTypeSelected" />
                    </div>
                </div>
            </div>
        </div>
        <b-row class="ad-report-options-row pt-3 d-flex flex-wrap justify-content-end">
            <b-col sm="12" md="auto" class="d-flex flex-column align-items-start mb-3">
                <label class="mb-1" for="dropdown-target-group">Target group</label>
                <b-dropdown :disabled="!graphIsLoaded" class="report-dropdown" size="sm" variant="outline-secondary" id="dropdown-target-group" :text="currentTargetGroup || 'Select target group'">
                    <b-dropdown-item v-for="(option, index) in targetGroups" :key="index" @click="setSelectedTargetGroup(option)">
                        {{ option }}
                    </b-dropdown-item>
                </b-dropdown>
            </b-col>
            <b-col sm="12" md="auto" class="d-flex flex-column justify-content-end mb-3 col-sm-12 col-md-auto">
                <b-button-group class="toggle-button-group">
                    <b-button
                        v-for="option in measureTypeOptions"
                        :key="option.value"
                        size="sm"
                        :variant="option.value === measureTypeSelected ? 'primary' : 'outline-secondary'"
                        @click="setActiveButton(option.value)"
                    >
                        {{ option.text }}
                    </b-button>
                </b-button-group>
            </b-col>

            <b-col sm="12" md="auto" class="d-flex flex-column align-items-start mb-3">
                <label class="mb-1" for="dropdown-measure-sum-mode">View mode</label>
                <b-dropdown :disabled="!tableIsLoaded" class="report-dropdown" size="sm" variant="outline-secondary" id="dropdown-measure-sum-mode" :text="selectedMeasureSumMode">
                    <b-dropdown-item v-for="(option, index) in tableViewModes" :key="index" @click="setTableMeasureSumMode(option)">
                        {{ option.label }}
                    </b-dropdown-item>
                </b-dropdown>
            </b-col>
        </b-row>

        <div class="ad-report-table-row">
            <ReportAdTable v-if="tableIsLoaded" :measureTypeSelected="measureTypeSelected" />
            <Spinner v-else class="custom-spinner" :loadingMsg="`Loading table for view mode: ${selectedMeasureSumMode}...`" />
        </div>
    </div>
</template>

<script>
import ReportAdGraph from "./ReportAdGraph.vue"
import ReportAdHeader from "./ReportAdHeader.vue"
import ReportAdTable from "./ReportAdTable.vue"
import * as Mutations from "@/store/modules/ads/mutation-types"
import Spinner from "@/components/Shared/Spinner/Spinner"
import { replace000sOrPercent } from "@/helpers/index.js"

export default {
    name: "ReportAd",
    components: {
        ReportAdGraph,
        ReportAdHeader,
        ReportAdTable,
        Spinner,
    },
    props: {
        tableViewModes: Array,
    },
    data() {
        return {
            initializedGraph: false,
            measureTypeOptions: [
                {
                    text: "Abs",
                    value: "absolute_reach",
                },
                {
                    text: "Rel",
                    value: "relative_reach",
                },
            ],
            activeButton: "",
            graphText: "",
        }
    },
    watch: {
        graphIsLoaded(newVal) {
            if (newVal) this.measureTypeSelected = "absolute_reach"
            if (newVal && !this.initializedGraph) {
                this.initializedGraph = true
                this.setGraphText()
                this.setSelectedTargetGroup(this.targetGroups[0])
            }
        },
    },
    computed: {
        measureTypeSelected: {
            get() {
                return this.$store.getters.getAdsCampaignReportMeasureTypeSelected
            },
            set(value) {
                this.$store.commit(Mutations.setAdsCampaignReportMeasureTypeSelected, value)
            },
        },
        targetGroups() {
            return this.$store.getters.getAdsCampaignReportQuery?.headerData?.targetGroups || []
        },
        currentTargetGroup() {
            return this.$store.getters.getAdsCampaignReportCurrentTargetGroup
        },
        selectedMeasureSumMode() {
            return this.$store.getters.getAdsCampaignReportMeasureSumMode.label
        },
        graphIsLoaded() {
            return this.$store.getters.getAdsCampaignReportResults.status === "LOADED"
        },
        tableIsLoaded() {
            return this.$store.getters.getAdsCampaignReportResults.data[this.$store.getters.getAdsCampaignReportMeasureSumMode.key].status === "LOADED"
        },
    },
    methods: {
        setGraphText() {
            this.$nextTick(() => {
                let reachText = this.measureTypeSelected === "absolute_reach" ? "Reach 000s" : "Reach %"
                this.graphText = `Total ${reachText} ${this.currentTargetGroup}`
            })
        },
        setActiveButton(measureTypeSelected) {
            this.measureTypeSelected = measureTypeSelected
            let measureColumns = this.$store.getters.getAdsCampaignReportResults.data.measureColumns ?? []
            measureColumns.forEach(column => {
                if (this.measureTypeSelected === "relative_reach" && !column.key.endsWith("_rel")) {
                    column.key += "_rel"
                    column.header = replace000sOrPercent(column.header)
                    column.label = `${column.header} ${column.subHeader}`
                    this.setGraphText()
                } else if (this.measureTypeSelected !== "relative_reach" && column.key.endsWith("_rel")) {
                    column.key = column.key.replace("_rel", "")
                    column.header = replace000sOrPercent(column.header)
                    column.label = `${column.header} ${column.subHeader}`
                    this.setGraphText()
                }
            })
        },
        setSelectedTargetGroup(targetGroup) {
            if (this.currentTargetGroup === targetGroup) return
            this.setGraphText()
            this.$store.commit(Mutations.setAdsCampaignReportCurrentTargetGroup, targetGroup)
        },
        async setTableMeasureSumMode(measureSumMode) {
            if (this.$store.getters.getAdsCampaignReportMeasureSumMode.key === measureSumMode.key) return
            this.$store.commit(Mutations.setAdsCampaignReportMeasureSumMode, measureSumMode)
        },
    },
}
</script>

<style scoped>
.btn-outline-secondary:hover,
.btn-group .btn-outline-secondary:active,
.btn-primary:focus {
    background-color: #337ab7;
    border-color: #337ab7;
    color: white;
}
.ad-report-container {
    font-size: 0.8rem;
}

.report-ad-header {
    margin-top: 1.85rem;
}
.measure-dropdown {
    max-width: 15rem;
}
.report-ad-header-and-graph {
    display: flex;
    flex-wrap: wrap;
}

.b-row {
    display: flex;
    flex-wrap: wrap;
}
.fixed-width {
    width: 21rem;
    flex: 0 0 auto;
    margin-right: 3rem;
    /* background-color: rgb(206, 168, 122); */
}
.graph-column {
    min-width: 35rem;
    /* background-color: rgb(54, 158, 158) !important; */
}

.gap-between {
    margin-left: 3rem;
    margin-right: 2rem;
}
.report-dropdown {
    min-width: 12rem !important;
}
.custom-spinner {
    font-size: 1rem;
}
@media (max-width: 576px) {
    .graph-column {
        min-width: 100%;
    }
    .fixed-width {
        margin-right: 0;
    }
}
</style>
